import { axios } from "@/config/axios";

// interfaces
import type {
  User,
  CandidateProfilesResponse,
  CandidateProfileDetails,
  ActiveRecruitmentsResponse,
  AllCompaniesResponse,
  UserFeatures,
} from "@/interfaces/User";
import type { CandidatesProfilesSearchQuery } from "@/interfaces/CandidatesProfilesSearch";
import type { SurveyAnswersResponse } from "@/interfaces/Surveys";

// enums
import { SurveyType } from "@/enums/SurveyType";
import { ApiResource } from "@/enums/ApiResource";

export const userApi = () => ({
  getUser: ({ companyId }: { companyId: string }): Promise<User> =>
    axios.get(`${ApiResource.PANEL_API}/${companyId}/users/me`),

  getFeatures: ({ companyId }: { companyId: string }): Promise<UserFeatures> =>
    axios.get(`${ApiResource.PANEL_API}/${companyId}/features`),

  logout: (): Promise<void> => axios.get("/logout"),

  getCandidateProfiles: ({
    companyId,
    queryParams,
  }: {
    companyId: string;
    queryParams: CandidatesProfilesSearchQuery;
  }): Promise<CandidateProfilesResponse> =>
    axios.get(`${ApiResource.PANEL_API}/${companyId}/candidate-profiles`, {
      params: { ...queryParams },
    }),

  getCandidateProfile: ({
    companyId,
    candidateId,
  }: {
    companyId: string;
    candidateId: string;
  }): Promise<CandidateProfileDetails> =>
    axios.get(
      `${ApiResource.PANEL_API}/${companyId}/candidate-profiles/${candidateId}`,
    ),

  getActiveRecruitments: ({
    companyId,
  }: {
    companyId: string;
  }): Promise<ActiveRecruitmentsResponse> =>
    axios.get(`${ApiResource.PANEL_API}/${companyId}/recruitments`),

  inviteCandidateToRecruitment: ({
    companyId,
    recruitmentId,
    userId,
    jobOfferId,
    cityId,
  }: {
    companyId: string;
    recruitmentId: string;
    userId: string;
    jobOfferId: string;
    cityId: string;
  }): Promise<ActiveRecruitmentsResponse> =>
    axios.post(`${ApiResource.PANEL_API}/${companyId}/candidates`, {
      recruitmentId,
      userId,
      jobOfferId,
      cityId,
    }),

  checkRecruitmentStatus: ({
    companyId,
    userId,
    jobOfferId,
  }: {
    companyId: string;
    userId: string;
    jobOfferId: string;
  }): Promise<any> =>
    axios.get(
      `${ApiResource.PANEL_API}/${companyId}/candidates/${userId}/participate-in-recruitment/${jobOfferId}`,
    ),

  getCandidateSurveyAnswers: ({
    companyId,
    userId,
  }: {
    companyId: string;
    userId: string;
  }): Promise<SurveyAnswersResponse> =>
    axios.get(
      `${ApiResource.PANEL_API}/${companyId}/candidate-profiles/${userId}/surveys/${SurveyType.CANDIDATE}`,
    ),

  getAllCompanies: ({
    companyId,
  }: {
    companyId: string;
  }): Promise<AllCompaniesResponse[]> =>
    axios.get(`${ApiResource.PANEL_API}/${companyId}/users/companies`),

  updateLastActivity: ({ companyId }: { companyId: string }): Promise<void> =>
    axios.put(`${ApiResource.ADMIN_API}/${companyId}/update-last-activity`),
});
